import { ThermostatAdjustment } from '../../../types/thermostatAdjustment'
import { match } from 'ts-pattern'

export type AdjustmentType =
  | 'save_money'
  | 'pre-cooling'
  | 'demand_response'
  | 'other'

export function getAdjustmentType(
  adjustment: ThermostatAdjustment | undefined | null,
): AdjustmentType {
  return match(adjustment)
    .with({ source: 'COST_CATEGORY' }, () => 'save_money')
    .with({ source: 'PRE_CONDITIONING' }, () => 'pre-cooling')
    .with({ source: 'DEMAND_RESPONSE' }, () => 'demand_response')
    .otherwise(() => 'other') as AdjustmentType
}

export interface SetpointChange {
  mode: 'heat' | 'cool'
  previous: number
  current: number
}

/**
 * Determines which setpoint an adjustment actually changed.
 *
 * A setpoint only counts as changed when both its current and previous values
 * are present (the backend serializes untouched setpoints as null) and differ.
 * An auto-mode thermostat reports both setpoints, so a cooling-only adjustment
 * must be detected from the cool pair even though a heat setpoint exists.
 * When both changed, cool wins — matching the cool-first precedence in
 * LastAdjustmentCardHeader and useThermostatStatusModes.
 */
export function getChangedSetpoints(
  adjustment: ThermostatAdjustment | undefined | null,
): SetpointChange | null {
  if (!adjustment) {
    return null
  }
  const {
    cool_setpoint,
    previous_cool_setpoint,
    heat_setpoint,
    previous_heat_setpoint,
  } = adjustment

  if (
    cool_setpoint != null &&
    previous_cool_setpoint != null &&
    cool_setpoint !== previous_cool_setpoint
  ) {
    return {
      mode: 'cool',
      previous: previous_cool_setpoint,
      current: cool_setpoint,
    }
  }
  if (
    heat_setpoint != null &&
    previous_heat_setpoint != null &&
    heat_setpoint !== previous_heat_setpoint
  ) {
    return {
      mode: 'heat',
      previous: previous_heat_setpoint,
      current: heat_setpoint,
    }
  }
  return null
}

/**
 * The setpoint pair the adjustment card should display, or null to show
 * no card.
 *
 * A pre-cool only ever targets the cool setpoint; its heat value is
 * backfill so an auto-mode thermostat can be commanded with both. A
 * changed heat pair on a PRE_CONDITIONING row is therefore never the
 * adjustment itself (DEV-6595: Nest rows carried a fabricated default
 * heat value), so precools display the cool pair when it changed and
 * nothing otherwise. Every other source keeps changed-pair behavior.
 */
export function getDisplaySetpoints(
  adjustment: ThermostatAdjustment | undefined | null,
): SetpointChange | null {
  const change = getChangedSetpoints(adjustment)
  if (getAdjustmentType(adjustment) !== 'pre-cooling') {
    return change
  }
  return change?.mode === 'cool' ? change : null
}
